import { AuthenticationService } from './../../_services/authentication.service';
import { ReportService } from './../../_services/report.service';
import { ReportGenerateData } from './../../_models/reports/report-generate-data.model';
import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ReportDataService } from '../report-data.service';
import { ColumnDef } from '../../shared/data-table/data-table.model';

import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { ReportType } from '../../_models/reports/report-type.model';
import { ReportCoordinatorFilter } from '../../_models/reports/report-coordinator-filter.model';
import { ReportTutorFilter } from '../../_models/reports/report-tutor-filter.model';
import { ReportStudentFilter } from '../../_models/reports/report-student-filter.model';
import { ReportClient } from '../../_models/reports/report-client.model';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { getNativeReportCode } from '../client-report-filter.util';
import { ClientReportService } from '../../_services/client-report.service';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { ReportFilterLookupsService } from '../report-filter-lookups.service';
import { buildNativeBatchRequest, buildSendListRequest, linesForLearners, requiredLines } from '../client-report-send';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, parseIsoDate, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';

@Component({
    selector: 'reports-generate',
    templateUrl: './reports-generate.component.html',
    styleUrls: ['./reports-generate.component.css'],
    standalone: false
})
export class ReportsGenerateComponent implements OnInit, OnDestroy {

  public frmReportGenerate: UntypedFormGroup;
  public errorMsg: string = "";
  public generated: boolean = false;
  public recordCount: number = null;
  public columns: ColumnDef<ReportGenerateData>[] = [];
  public reportRows: Array<ReportGenerateData> = new Array<ReportGenerateData>();

  // Grey out rows that don't require a report (restores the pre-migration row styling).
  public rowStyle = (row: ReportGenerateData) =>
    row.reportRequired && row.reportRequired.toLowerCase() === 'no'
      ? { 'background-color': 'lightgrey' }
      : null;

  public filteredReports: Array<ReportType> = new Array<ReportType>();
  public filteredCoordinatorList: Array<ReportCoordinatorFilter> = new Array<ReportCoordinatorFilter>();
  public filteredTutorList: Array<ReportTutorFilter> = new Array<ReportTutorFilter>();
  public filteredStudentList: Array<ReportStudentFilter> = new Array<ReportStudentFilter>();
  public filteredClientList: Array<ReportClient> = new Array<ReportClient>();

  public sendReportList: Array<ReportGenerateData> = new Array<ReportGenerateData>();
  private generatedReportList: Array<ReportGenerateData> = new Array<ReportGenerateData>();

  public studentTooltip: string

  public brandList: BrandInfo[] = [];
  /** The batch format choice (reports.batch-pdf). Off until the switch says on; Excel unless PDF is picked. */
  public batchPdfEnabled = false;
  public batchFormat: 'xlsx' | 'pdf' = 'xlsx';
  private selectedStudentIds: number[] = [];

  private unsubscribe: Subject<void> = new Subject<void>();

  /** Loads the filter lists for the chosen client; ReportLookupNoteComponent shows a failed one. */
  private filterLookups: ReportFilterLookupsService;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public reportDataService: ReportDataService,
    private reportService: ReportService,
    private authService: AuthenticationService,
    private successModalHelper: SuccessModalHelperService,
    public themeService: ThemeService,
    private errorModalHelper: ErrorModalHelperService,
    private clientReportService: ClientReportService,
    @Optional() private featureSwitches?: FeatureSwitchService,
    @Optional() filterLookups?: ReportFilterLookupsService,
    @Optional() private router: Router = null,
    @Optional() private route: ActivatedRoute = null
  ) {
    this.filterLookups = filterLookups ?? new ReportFilterLookupsService(reportService, reportDataService);
  }

  /** True when the selected report runs natively (only those can be generated as PDF). */
  public get isNativeReportSelected(): boolean {
    return getNativeReportCode(this.frmReportGenerate?.value?.reports) != null;
  }

  /** The Send button names the format it will produce while the batch format choice is on. */
  public get sendReportsLabel(): string {
    if (!this.batchPdfEnabled) {
      return 'Send reports';
    }
    return this.batchFormat === 'pdf' ? 'Send reports (PDF)' : 'Send reports (Excel)';
  }

  /**
   * The filters.reports-generate switch: date presets, remember-my-filters, Clear all, filter state in the URL and
   * the filter-bar layout, as on Reports Sent. Off, the screen is exactly as it was.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersReportsGenerate;
  public filtersEnabled: boolean = false;
  /** The date preset behind the start and end dates, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates only:
   * names stay out of the URL and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    from: 'string', to: 'string', preset: 'string', report: 'string', client: 'number', coordinator: 'number',
    tutor: 'number', student: 'number[]', brand: 'number[]'
  };
  /** Remembered filters and filter state in the URL; used only while filters.reports-generate is on. */
  private filterUx: ScreenFilterUx;
  /** Filters to restore once both the switch and the report types have arrived. */
  private pendingFilterState: FilterState = null;

  ngOnInit() {
    this.filterUx = new ScreenFilterUx(this.filtersKey, ReportsGenerateComponent.filterSpec, this.router, this.route);

    let defaultEndDate = new Date();
    let defaultStartDate = new Date(defaultEndDate.getTime() - (30*24*60*60*1000));

    this.frmReportGenerate = this.formBuilder.group({
      startDate: [defaultStartDate, [Validators.required]],
      endDate: [defaultEndDate, [Validators.required]],
      reports: [null, [Validators.required]],
      coordinators: [null],
      clients: [null],
      tutors: [null],
      students: [null],
      brands: [null]
    });

    this.columns = [
      { field: 'placementID', header: 'PlacementId' },
      { field: 'clientName', header: 'Client name' },
      { field: 'referrerName', header: 'Referrer name' },
      { field: 'tutorName', header: 'Tutor name' },
      { field: 'studentName', header: 'Learner' },
      { field: 'sendReport', header: 'Send report', type: 'template' }
    ];

    this.reportDataService.reportListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.filteredReports = this.reportDataService.reports;
      this.applyPendingFilterState();
    });
    this.reportDataService.clientListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => this.filteredClientList = this.reportDataService.clientList);

    setTimeout(() => {
      this.studentTooltip = "Please select a learner";
    }, 100);

    this.featureSwitches?.isEnabled(FeatureSwitchKeys.clientReportsBatchPdf).pipe(takeUntil(this.unsubscribe))
      .subscribe(enabled => this.batchPdfEnabled = enabled === true);
    // Only native reports have a PDF version, so a switch to any other report falls back to Excel.
    this.frmReportGenerate.controls['reports'].valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe(report => {
        if (getNativeReportCode(report) == null) {
          this.batchFormat = 'xlsx';
        }
      });

    // The selection is validated against the report API's own brand set, so the brand list comes from there.
    this.clientReportService.getBrands().pipe(takeUntil(this.unsubscribe)).subscribe((data: BrandInfo[]) => {
      this.brandList = data != null ? data : [];
    });

    // After the form exists: a restored filter may load the list straight away.
    this.featureSwitches?.isEnabled(this.filtersKey).pipe(takeUntil(this.unsubscribe)).subscribe(enabled => {
      this.filtersEnabled = enabled === true;
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = this.filtersEnabled ? this.filterUx.initialState() : null;
      this.applyPendingFilterState();
    });
  }

  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. The list is left as it is. */
  public clearAll(): void {
    const defaultEndDate = new Date();
    this.frmReportGenerate.reset({
      startDate: new Date(defaultEndDate.getTime() - (30*24*60*60*1000)),
      endDate: defaultEndDate
    });
    this.datePreset = null;
    this.filterUx.clear();
  }

  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.frmReportGenerate.patchValue({ startDate: parseIsoDate(selection.from), endDate: parseIsoDate(selection.to) });
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.frmReportGenerate.value;
    const students = value.students == null ? [] : (Array.isArray(value.students) ? value.students : [value.students]);
    return {
      preset: this.datePreset,
      from: this.datePreset || !value.startDate ? null : toIsoDate(new Date(value.startDate)),
      to: this.datePreset || !value.endDate ? null : toIsoDate(new Date(value.endDate)),
      report: value.reports?.name ?? null,
      client: value.clients,
      coordinator: value.coordinators,
      tutor: value.tutors,
      // Learner ids only: by name, the legacy learner filter would put a name in the URL.
      student: students.filter((id: any) => typeof id === 'number'),
      brand: value.brands ?? []
    };
  }

  /**
   * Restores remembered or linked filters. The report type is an object from the report list, so this waits for
   * the list. With a report type restored the list loads, as it would after Generate.
   */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || !this.frmReportGenerate || (this.reportDataService.reports ?? []).length === 0) {
      return;
    }
    this.pendingFilterState = null;

    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    const from = parseIsoDate(range ? range.from : state.from as string);
    const to = parseIsoDate(range ? range.to : state.to as string);
    const report = this.reportDataService.reports.find(r => r.name === state.report) ?? null;
    const students = (state.student as number[]) ?? [];
    this.frmReportGenerate.patchValue({
      startDate: from ?? this.frmReportGenerate.value.startDate,
      endDate: to ?? this.frmReportGenerate.value.endDate,
      reports: report,
      clients: (state.client as number) ?? null,
      coordinators: (state.coordinator as number) ?? null,
      tutors: (state.tutor as number) ?? null,
      students: students.length > 0 ? students : null,
      brands: ((state.brand as number[]) ?? []).length > 0 ? state.brand : null
    });
    if (state.client != null) {
      this.onClientSelected(state.client);
    }

    if (report) {
      this.generateReportList();
    }
  }

  /** The Brand control's selection; empty means every brand the user may see. */
  private selectedCompanyIds(): number[] {
    const brands = this.frmReportGenerate.value.brands;
    return brands != null && brands.length > 0 ? brands : [];
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public generateReportList() {
    if (this.frmReportGenerate.invalid) {
      return;
    }
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }

    const requester = {
      userId: this.authService.currentUserValue.id,
      email: this.authService.userSessionData?.currentUser?.email,
      clientUsers: this.reportDataService.clientUser
    };
    const { params, studentIds } = buildSendListRequest(this.frmReportGenerate.value, this.frmReportGenerate.value.clients,
      this.selectedCompanyIds(), requester);
    this.selectedStudentIds = studentIds;
    this.reportService.generateReportListToSend(params).subscribe((data: ReportGenerateData[]) => {
      data = linesForLearners(data, this.selectedStudentIds);

      this.recordCount = data.length;
      this.reportRows = data;

      this.generatedReportList = data;
      this.sendReportList = requiredLines(data);
    },
      error => {
        this.errorModalHelper.show("An error has occurred, please try again", error);
        console.error(error);
      }
    );
  }

  public processSendReportRowCLick(item: ReportGenerateData, event: any): void {
    if (event.checked) {
      this.sendReportList.push(item);
    } else {
      let itemIndex = this.sendReportList.indexOf(item);
      this.sendReportList.splice(itemIndex, 1);
    }
  }

  public sendReports(): void {
    const formValue = this.frmReportGenerate.value;
    const nativeCode = getNativeReportCode(formValue.reports);
    const sent = () => {
      this.successModalHelper.show("Successfully generated reports");
      this.recordCount = 0;
      this.reportRows = new Array<ReportGenerateData>();
    };
    const failed = (error: any) => {
      this.errorModalHelper.show("An error has occurred, please try again", error);
      console.error(error);
    };
    if (nativeCode == null) { return; }
    const request = buildNativeBatchRequest(formValue, nativeCode, this.selectedStudentIds, this.sendReportList,
      this.batchPdfEnabled && this.batchFormat === 'pdf');
    this.clientReportService.runBatch(request).subscribe(sent, failed);
  }
  public clearMoreOptions(): void {
    this.frmReportGenerate.controls['coordinators'].reset();
    this.frmReportGenerate.controls['clients'].reset();
    this.frmReportGenerate.controls['tutors'].reset();
    this.frmReportGenerate.controls['students'].reset();
    if (this.frmReportGenerate.controls['brands'] != null) {
      this.frmReportGenerate.controls['brands'].reset();
    }
  }

  public onClientSelected(value: any): void {
    // A lookup that fails leaves its list empty with an inline Retry note, never an error popup (40760).
    this.filterLookups.load(value);
  }

}
